package com.arena_web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.data.api.AnalyticsApi
import com.arena_web.data.websocket.UserWsEvent
import com.arena_web.di.LocalAppContainer
import com.arena_web.di.ProvideAppContainer
import com.arena_web.pages.NotFoundPage
import com.arena_web.ui.components.notifications.LocalToastState
import com.arena_web.ui.components.notifications.NotificationSnackbarHost
import com.arena_web.ui.components.notifications.NotificationSnackbarState
import com.arena_web.ui.components.notifications.ProvideToast
import com.arena_web.ui.components.widgets.CookieConsent
import com.arena_web.ui.localization.ProvideLanguage
import com.arena_web.ui.localization.rememberLanguage
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.initAppTheme
import com.arena_web.utils.OAuthHelper
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.core.init.InitKobweb
import com.varabyte.kobweb.core.init.InitKobwebContext
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.style.breakpoint.BreakpointSizes
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

// Kobweb's `@Page("/404")` only matches the literal `/404` path — for every
// other unmatched URL the framework falls back to its built-in "Page not
// found" placeholder. Registering the page via `Router.setErrorPage` makes the
// custom Figma-spec 404 screen render for any unknown route while preserving
// the URL the user typed (no redirect to `/404`).
@InitKobweb
fun initKobweb(ctx: InitKobwebContext) {
    ctx.router.setErrorPage { NotFoundPage() }

    // Central analytics screen-tracking hook — Kobweb runs route interceptors
    // for the initial page load and every internal `navigateTo`, so this one
    // registration covers all screens (browser back/forward is caught by the
    // tracker's own `popstate` listener). The path is only read, never
    // modified, and the tracker dedupes consecutive repeats.
    ctx.router.addRouteInterceptor {
        AnalyticsTracker.onRouteChanged(path)
    }
}

@InitSilk
fun initSilk(ctx: InitSilkContext) {

    // App is designed dark-first; hard-lock to DARK everywhere so a stale
    // `Storage.THEME = "LIGHT"` from past experiments (or any future code
    // path that flips ColorMode) can't push the UI into a half-styled
    // light state. The ThemeToggle widget is intentionally unused — every
    // `ColorMode.current` consumer reads DARK and never branches into
    // light styles. Also wipe any stale THEME value so the storage stays
    // tidy.
    runCatching { Storage.remove(StorageKeys.THEME) }
    ctx.config.initialColorMode = ColorMode.DARK

    ctx.theme.breakpoints = BreakpointSizes(
        sm = 0.px,
        md = 768.px,
        lg = 1280.px,
        xl = 1920.px
    )

    // Set Silk palette to match our design system
    ctx.theme.palettes.light.background = AppColors.Light.Background
    ctx.theme.palettes.light.color = AppColors.Light.TextPrimary
    ctx.theme.palettes.dark.background = AppColors.Dark.Background
    ctx.theme.palettes.dark.color = AppColors.Dark.TextPrimary

    initAppTheme(ctx)
}

@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
    LaunchedEffect(Unit) {
        // Reveal the page now that Compose has mounted and replaced the
        // pre-rendered static DOM. The `arena-preload` class is added by an
        // inline script in <head> (see site/build.gradle.kts) to hide the
        // static export during hydration so users don't see a flash + wipe.
        kotlinx.browser.document.documentElement?.classList?.remove("arena-preload")

        // Allow Google avatar images (lh3.googleusercontent.com) to load without referrer blocking
        val meta = kotlinx.browser.document.createElement("meta")
        meta.setAttribute("name", "referrer")
        meta.setAttribute("content", "no-referrer-when-downgrade")
        kotlinx.browser.document.head?.appendChild(meta)

        OAuthHelper.loadGoogleSDK {
            console.log("Google SDK loaded successfully")
        }

        // Resolve the user's coarse location once per page load (GPS only if
        // the permission is already granted — no prompt on load; IP otherwise)
        // so the address autocomplete bias reads it from cache synchronously.
        com.arena_web.utils.prefetchUserGeo()
    }

    ProvideAppContainer {
        // Restore authenticated session from localStorage on page load (synchronous —
        // must run during first composition so child pages see userRepository != null)
        val container = LocalAppContainer.current
        remember {
            val token = Storage.get(StorageKeys.ACCESS_TOKEN)
            if (token != null) {
                container.createAuthenticatedClients(token)
            }
            // One-time analytics wiring — reuses the container's default Ktor
            // client (the endpoint accepts anonymous batches; the API adds the
            // Bearer token from localStorage when present). Idempotent, so a
            // hot-reload remount can't double-register handlers.
            AnalyticsTracker.init(AnalyticsApi(container.defaultHttpClient))
        }

        val languageState = rememberLanguage()

        key("silk-app") {
            SilkApp {
                ProvideToast {
                    ProvideLanguage(languageState) {
                        val colorMode = ColorMode.current
                        val colors = AppColors.getColors(colorMode)

                        // Global top banner for real-time WS events —
                        // notifications and incoming chat messages.
                        val toastState = LocalToastState.current
                        val strings = com.arena_web.ui.localization.LocalAppStrings.current

                        // Settle a coin purchase left pending by the redirect
                        // to mono's payment page (or by a closed tab): pull-
                        // confirm the order, refresh the balance and toast the
                        // outcome. The webhook credits server-side either way.
                        LaunchedEffect(Unit) {
                            val walletRepo = container.walletRepository ?: return@LaunchedEffect
                            when (val result = com.arena_web.ui.components.overlay.resumePendingTopUp(walletRepo)) {
                                is com.arena_web.ui.components.overlay.PendingTopUpResult.Credited -> {
                                    container.refreshWallet()
                                    toastState.success(strings.walletCreditsAdded(result.coins))
                                }
                                is com.arena_web.ui.components.overlay.PendingTopUpResult.Failed -> {
                                    toastState.error(strings.walletPurchaseFailed)
                                }
                                null -> {}
                            }
                        }
                        val notificationSnackbar = remember { NotificationSnackbarState() }
                        LaunchedEffect(Unit) {
                            if (!Storage.contains(StorageKeys.ACCESS_TOKEN)) return@LaunchedEffect
                            container.userWsClient.events.collect { event ->
                                when (event) {
                                    is UserWsEvent.NewNotification -> {
                                        // Rewards have no actor — show the diamond icon
                                        // instead of an initials card (same rule as the
                                        // notifications panel).
                                        val isReward =
                                            event.notificationType?.uppercase() == "REWARD_RECEIVED"
                                        notificationSnackbar.show(
                                            title = event.title,
                                            body = event.body,
                                            avatarUrl = if (isReward) null else event.actorAvatarUrl,
                                            cardAssetsBasePath =
                                                if (isReward) null else event.actorCardAssetsBasePath,
                                            fallbackIcon = if (isReward) AppIcons.Diamond else null,
                                        )
                                    }
                                    is UserWsEvent.ChatListUpdate -> {
                                        if (event.senderName != null) {
                                            notificationSnackbar.show(
                                                title = event.senderName,
                                                body = event.preview,
                                                avatarUrl = event.senderAvatarUrl,
                                                cardAssetsBasePath = event.senderCardAssetsBasePath,
                                            )
                                        } else {
                                            // Old backend payload without sender info.
                                            toastState.info("New message", duration = 2000L)
                                        }
                                    }
                                    else -> {}
                                }
                            }
                        }

                        Box(
                            Modifier
                                .fillMaxWidth()
                                .minHeight(100.vh)
                                .scrollBehavior(ScrollBehavior.Smooth)
                                .color(colors.textPrimary)
                        ) {
                            content()
                            CookieConsent()
                            // Mobile "another user's profile" bottom sheet —
                            // overlays any page when opened via openUserProfile().
                            com.arena_web.ui.sections.profile.mobile.UserProfileSheetHost()
                            NotificationSnackbarHost(notificationSnackbar)
                        }
                    }
                }
            }
        }
    }
}

